import React, { useEffect, useRef } from 'react';
import { Snackbar as SnackbarUnion } from '@xo-union/tk-component-alerts';
import Icon from '@xo-union/tk-component-icons';
import { compose } from '@xo-union/react-css-modules';

import styles from './snackbar.scss';

export const DISMISS_TIMEOUT = 2500;
export const LONG_DISMISS_TIMEOUT = 5000;
export const SNACKBAR_DOMAIN_PAYMENT_TYPE = 'DOMAIN_PAYMENT';

export interface SnackbarMessage {
  message: string;
  type?: string;
  dismissTimeout?: number;
}

interface SnackbarProps {
  toastMessage?: SnackbarMessage;
  clearToastMessage?: (message: SnackbarMessage) => void;
}

interface SnackbarInterface {
  showAndDismiss: (ops: any) => void;
}

/**
 * The snackbar component is a wrapper for the tk-component-alerts Snackbar component.
 * It is used to display a message to the user for a short period of time.
 *
 * @param {SnackbarProps} { clearToastMessage, toastMessage }
 * @returns {JSX.Element}
 */
export function Snackbar({ clearToastMessage, toastMessage }: SnackbarProps) {
  const snackbar = useRef<SnackbarInterface>();

  useEffect(() => {
    if (!!toastMessage?.message) {
      snackbar.current?.showAndDismiss({
        data: {
          text: toastMessage.message,
          type: toastMessage.type,
        },
        dismissTimeout: toastMessage.dismissTimeout || DISMISS_TIMEOUT,
      });
      clearToastMessage && clearToastMessage({ message: '', type: '' });
    }
  }, [toastMessage, clearToastMessage]);

  function generateDisplayText(data: { type: string; text: string; dismissTimeout: number | undefined }) {
    switch (data?.type) {
      case SNACKBAR_DOMAIN_PAYMENT_TYPE:
        return (
          <>
            <div className={styles.paymentConfirmationSnackbar}>
              <span className={styles.iconPaymentConfirmation}>
                <Icon size="md" name="checkmark" />
              </span>
              <span className={styles.paymentConfirmationSnackbarText}>{data?.text}</span>
            </div>
          </>
        );
      default:
        return <>{data?.text}</>;
    }
  }

  const snackBarClasses = compose({
    container: styles['snackBarContainer'],
  });

  return (
    <SnackbarUnion classes={snackBarClasses} ref={snackbar}>
      {(data: { type: string; text: string; dismissTimeout: number | undefined }) => {
        return generateDisplayText(data);
      }}
    </SnackbarUnion>
  );
}
